import React, { useState } from "react";
import { Trash2, AlertTriangle } from "lucide-react";
import Modal from "./Modal";
import { toast } from "react-toastify";
import supabase from "../../utils/supabase";
import { useT } from "../../stores/uiLang";

interface Class {
  id: string;
  name: string;
  description?: string;
  semester: string;
  settings: {
    class_time: string;
  };
  grade_type: "ABC" | "1234" | "%";
  student_count?: number;
  google_class_id?: string;
  last_synced?: string;
}

interface ConfirmDeleteClassModalProps {
  isOpen: boolean;
  onClose: () => void;
  cls: Class | null;
  onClassDeleted: () => void;
}

// Run E3 B4: this modal follows the UI language (EN text unchanged).
const CDC_T = {
  en: {
    failedWith: "Failed to delete class: ",
    deleted: "Class deleted successfully",
    failed: "Failed to delete class",
    title: "Delete Class",
    sure: "Are you sure you want to delete this class?",
    body: "This action cannot be undone. The class and all associated data will be permanently removed.",
    semester: "Semester:",
    description: "Description:",
    gradeType: "Grade Type:",
    cancel: "Cancel",
    deleting: "Deleting...",
    del: "Delete Class",
  },
  es: {
    failedWith: "No se pudo eliminar la clase: ",
    deleted: "La clase se eliminó",
    failed: "No se pudo eliminar la clase",
    title: "Eliminar la clase",
    sure: "¿Seguro que quieres eliminar esta clase?",
    body: "No se puede deshacer. La clase y todos sus datos se borrarán para siempre.",
    semester: "Semestre:",
    description: "Descripción:",
    gradeType: "Tipo de calificación:",
    cancel: "Cancelar",
    deleting: "Eliminando...",
    del: "Eliminar la clase",
  },
};

const ConfirmDeleteClassModal: React.FC<ConfirmDeleteClassModalProps> = ({
  isOpen,
  onClose,
  cls,
  onClassDeleted,
}) => {
  const t = useT(CDC_T);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    if (!cls) return;

    setIsDeleting(true);
    try {
      const { error: classError } = await supabase
        .from("classes")
        .delete()
        .eq("id", cls.id);

      if (classError) {
        console.error("Error deleting class:", classError);
        toast.error(t.failedWith + classError.message);
        return;
      }

      toast.success(t.deleted);
      onClassDeleted();
      onClose();
    } catch (error) {
      console.error("Error deleting class:", error);
      toast.error(t.failed);
    } finally {
      setIsDeleting(false);
    }
  };

  if (!cls) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t.title}>
      <div className="space-y-4">
        {/* Warning Icon */}
        <div className="flex items-center justify-center">
          <div className="flex items-center justify-center w-12 h-12 bg-red-100 rounded-full">
            <AlertTriangle className="w-6 h-6 text-red-600" />
          </div>
        </div>

        {/* Warning Message */}
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {t.sure}
          </h3>
          <p className="text-sm text-gray-600">
            {t.body}
          </p>
        </div>

        {/* Class Info */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="text-sm">
            <div className="font-medium text-gray-900">{cls.name}</div>
            <div className="text-gray-600">{t.semester} {cls.semester}</div>
            {cls.description && (
              <div className="text-gray-600">
                {t.description} {cls.description}
              </div>
            )}
            <div className="text-gray-600">{t.gradeType} {cls.grade_type}</div>
            {cls.google_class_id && (
              <div className="text-gray-600">
                <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                  Google Classroom
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 pt-4">
          <button
            onClick={onClose}
            disabled={isDeleting}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t.cancel}
          </button>
          <button
            onClick={handleDelete}
            disabled={isDeleting}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isDeleting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                {t.deleting}
              </>
            ) : (
              <>
                <Trash2 className="w-4 h-4 mr-2" />
                {t.del}
              </>
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ConfirmDeleteClassModal;
